import { getTown } from '@wyn/content';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { bankQuery, useAction } from '../lib/queries.ts';
import { coin, formatDuration, useNow } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const BANK_TOWN = 'wynchester';
const pct = (r: number) => `${(r * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`;

/** The Royal Bank of Wynchester: coin here cannot be robbed, and locked coin earns interest. */
export function Bank() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const p = me.data.player;
  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        <section>
          <PageTitle icon={UI.bank}>The Royal Bank</PageTitle>
          <p className="text-ink-dim">Coin in the bank is safe from thieves. Lock it away for a term and the Crown pays interest.</p>
        </section>
        {p.town !== BANK_TOWN || p.status.kind === 'travelling' ? (
          <section className="panel space-y-2">
            <p>The Royal Bank keeps its doors in {getTown(BANK_TOWN).name}. Take the road there to bank your coin.</p>
            <Link to="/town" className="btn inline-block">Back to town</Link>
          </section>
        ) : (
          <>
            <Balances />
            <Counter />
            <Locks />
          </>
        )}
      </div>
    </GameShell>
  );
}

function Balances() {
  const p = useMe().data!.player!;
  return (
    <section className="panel grid grid-cols-3 gap-2 text-center">
      {[
        ['Purse', p.purse],
        ['Bank', p.bank],
        ['Locked', p.locked],
      ].map(([label, n]) => (
        <div key={label}>
          <div className="smallcaps text-sm text-ink-dim">{label}</div>
          <div className="font-mono text-lg tabular-nums text-gilt" data-testid={`balance-${String(label).toLowerCase()}`}>{coin(n as number)}</div>
        </div>
      ))}
    </section>
  );
}

function Counter() {
  const dep = useAction('deposit', { refetch: ['bank'] });
  const wd = useAction('withdraw', { refetch: ['bank'] });
  const [amount, setAmount] = useState(100);
  const p = useMe().data!.player!;
  const busy = dep.isPending || wd.isPending;
  return (
    <section className="panel space-y-3">
      <h2 className="heading">At the counter</h2>
      <div className="flex flex-wrap items-center gap-2">
        <input className="field w-32 font-mono" type="number" min={1} aria-label="Amount" value={amount} onChange={(e) => setAmount(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        <button type="button" className="btn" disabled={busy || amount > p.purse} onClick={() => dep.mutate({ amount })}>Deposit</button>
        <button type="button" className="btn" disabled={busy || amount > p.bank} onClick={() => wd.mutate({ amount })}>Withdraw</button>
        <button type="button" className="btn-quiet" onClick={() => setAmount(Math.max(1, p.purse))}>whole purse</button>
      </div>
      <ErrorNotice error={dep.error ?? wd.error} />
    </section>
  );
}

function Locks() {
  const q = useQuery(bankQuery());
  const lock = useAction('lock', { refetch: ['bank'] });
  const [amount, setAmount] = useState(500);
  const now = useNow();
  const me = useMe();
  const p = me.data!.player!;
  const rates = q.data?.rates ?? [];
  return (
    <section className="panel space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Locked coin</h2>
        <span className="text-sm text-ink-faint">Paid back with interest at the first midnight rollover after it matures.</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className="field w-32 font-mono" type="number" min={100} aria-label="Amount to lock" value={amount} onChange={(e) => setAmount(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        {rates.map((r) => (
          <button key={r.days} type="button" className="btn" disabled={lock.isPending || amount > p.bank} onClick={() => lock.mutate({ amount, days: r.days })}>
            {r.days} days · {pct(r.rate)}
          </button>
        ))}
      </div>
      <p className="text-xs text-ink-faint">Locks come from the bank, not the purse. At most three at once, 100 coin or more each.</p>
      {(q.data?.locks.length ?? 0) > 0 && (
        <ul className="space-y-1 text-sm">
          {q.data!.locks.map((l) => (
            <li key={l.id} className="flex flex-wrap justify-between gap-2 border-b border-rule/60 pb-1 last:border-0">
              <span>{coin(l.amount)} for {l.days} days, earning {coin(l.interest)}</span>
              <span className="font-mono text-xs text-ink-faint">{l.maturesAt > now + me.skew ? `matures in ${formatDuration(l.maturesAt - (now + me.skew))}` : 'matured · pays at rollover'}</span>
            </li>
          ))}
        </ul>
      )}
      <ErrorNotice error={lock.error ?? q.error} />
    </section>
  );
}
